// Admin referral commissions (/admin/commissions) — cross-referrer ledger of
// commissions (rate stored per row) accrued once per paid order of someone who
// signed up via a referral code. Sales are closed (DECISIONS D36), so the rows
// are history. Payout is manual: the row actions (Tandai dibayar / Void) just
// flip status + audit; money moves outside this system.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Glyph } from "@/ui";
import { dataClass } from "@ahcut/ui/recipes";
import { Badge } from "@/ui";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { Pagination } from "@/components/Pagination";
import { FilterSelect } from "@/components/FilterSelect";
import { CommissionAdminActions } from "@/components/CommissionAdminActions";
import { useAsync } from "@/lib/useAsync";
import { listAdminCommissions } from "@/lib/admin";
import {
  bpsToPercentLabel,
  formatDateTime,
  formatRupiah,
  referralCommissionStatusLabel,
  referralCommissionStatusOptions,
  referralCommissionStatusTone,
} from "@/lib/format";
import type { AdminCommission, Paged, ReferralCommissionStatus } from "@/lib/types";
import { AdminPanel } from "@/components/admin/AdminPanel";
import {
  DataCell,
  DataRow,
  DataTable,
  FilterBar,
  type DataColumn,
} from "@/components/admin/DataTable";
import { dataCellClass } from "@/components/admin/classes";
import { BreakableText } from "@/components/admin/BreakableText";

const PAGE = 25;
const STATUS_OPTIONS = referralCommissionStatusOptions();

// The payout note sits under its status (a note is written when a row is marked
// paid or void), which keeps both row actions visible beside the sidebar.
const COLUMNS: readonly DataColumn[] = [
  { label: "Tanggal" },
  { label: "Referral" },
  { label: "Pesanan" },
  { label: "Nilai", align: "end" },
  { label: "Status" },
  { label: "Aksi" },
];

export function AdminCommissions() {
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, loading, error, reload } = useAsync<Paged<AdminCommission>>(
    (s) =>
      listAdminCommissions(
        { status: status as ReferralCommissionStatus | "", limit: PAGE, offset },
        s,
      ),
    [status, offset],
  );

  if (loading && !data) return <Loading label="Memuat komisi…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Komisi referral"
        lede="Riwayat komisi dari pesanan lunas orang yang direferalkan; penjualan sudah ditutup. Payout manual — tandai dibayar setelah transfer dilakukan di luar sistem."
      />

      <FilterBar label="Filter komisi">
        <FilterSelect
          label="Status"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(v) => {
            setOffset(0);
            setStatus(v);
          }}
        />
      </FilterBar>

      {!data || data.items.length === 0 ? (
        <EmptyState title="Belum ada komisi">Tidak ada komisi untuk filter ini.</EmptyState>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <AdminPanel>
            <DataTable
              label="Daftar komisi referral"
              columns={COLUMNS}
              stackBelow="58rem"
              busy={loading}
            >
              {data.items.map((c) => (
                <DataRow key={c.id}>
                  <DataCell label="Tanggal" className={dataCellClass}>
                    {formatDateTime(c.created_at)}
                  </DataCell>
                  <DataCell label="Referral">
                    {/* Referrer on top, the referred account indented under it.
                        An email breaks only before its "@" when the table is tight. */}
                    <span className="flex flex-col gap-1">
                      <span>
                        <span className="sr-only">Referrer: </span>
                        <Link to={`/admin/users/${c.referrer_user_id}`}>
                          <BreakableText text={c.referrer_email} at="email" />
                        </Link>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Glyph name="cornerDownRight" className="size-3.5 shrink-0 text-fg-2" />
                        <span className="sr-only">Direferalkan: </span>
                        <span className="min-w-0">
                          <Link to={`/admin/users/${c.referred_user_id}`}>
                            <BreakableText text={c.referred_email} at="email" />
                          </Link>
                        </span>
                      </span>
                    </span>
                  </DataCell>
                  <DataCell label="Pesanan">
                    <Link to={`/admin/orders/${c.order_id}`}>
                      Lihat<span className="sr-only"> pesanan {c.order_id}</span>
                    </Link>
                  </DataCell>
                  {/* Block lines follow the cell's alignment: right in table
                      layout, left once the rows stack. */}
                  <DataCell label="Nilai" align="end" className="whitespace-nowrap">
                    <span className={cn(dataClass, "block text-fg-0")}>{formatRupiah(c.amount)}</span>
                    <span className="mt-0.5 block text-xs text-fg-2">
                      Tarif <span className={dataClass}>{bpsToPercentLabel(c.rate_bps)}</span>
                    </span>
                  </DataCell>
                  <DataCell label="Status">
                    <span className="flex flex-col items-start gap-1">
                      <Badge tone={referralCommissionStatusTone(c.status)} dot>
                        {referralCommissionStatusLabel(c.status)}
                      </Badge>
                      {c.note && (
                        <span className="max-w-[32ch] text-xs text-fg-2">
                          <span className="sr-only">Catatan: </span>
                          {c.note}
                        </span>
                      )}
                    </span>
                  </DataCell>
                  <DataCell actions>
                    <CommissionAdminActions commission={c} onDone={reload} />
                  </DataCell>
                </DataRow>
              ))}
            </DataTable>
          </AdminPanel>
          <Pagination
            total={data.total}
            limit={data.limit}
            offset={data.offset}
            onPage={setOffset}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}
